/**
 * Deterministic, seeded randomness for the engine core (architecture.md §3.4,
 * decision D08).
 *
 * @remarks
 * `Math.random` is banned in the pure core — determinism is a contract, not a
 * vibe. Randomness is instead pure state-in/state-out: every draw consumes a
 * {@link RandomnessState} and returns the value **plus the next state**, which
 * the turn pipeline threads through `RuntimeState`. Save → load → identical
 * future, including "random" outcomes; replays get this for free.
 *
 * The {@link RandomnessState} TYPE is owned by `@orpheus/content-schema`'s
 * runtime-state schemas (Mike's R1 ruling, 2026-08-12: schemas own the
 * shapes; this module keeps the pure helpers) and re-exported here so engine
 * consumers keep one import surface.
 *
 * The generator is **mulberry32** — tiny, fast, and ample quality for game
 * logic (variant narration, foe behavior, future combat). It is deliberately
 * not a cryptographic PRNG and must never be used for one.
 *
 * @packageDocumentation
 */

import type { RandomnessState } from '@orpheus/content-schema';

export type { RandomnessState } from '@orpheus/content-schema';

/** One draw's result: the value and the state every subsequent draw must use. */
export interface RandomDraw {
	/** The state to thread into the next draw. */
	readonly nextState: RandomnessState;
	/** A uniform value in `[0, 1)`. */
	readonly value: number;
}

/** One integer draw's result: the value and the state every subsequent draw must use. */
export interface RandomIntegerDraw {
	/** The state to thread into the next draw. */
	readonly nextState: RandomnessState;
	/** A uniform integer in `[minInclusive, maxInclusive]`. */
	readonly value: number;
}

/** The mulberry32 additive constant. */
const MULBERRY32_INCREMENT = 0x6d2b79f5;

/** Divisor mapping a uint32 onto `[0, 1)`. */
const UINT32_RANGE = 2 ** 32;

/**
 * Create the initial randomness state for a session.
 *
 * @param seed - Any finite number; truncated and wrapped to a uint32. Record it — `(bundle, seed, commands)` is the whole replay recipe.
 * @returns A fresh state with `drawCount` 0
 * @throws RangeError if `seed` is not a finite number (programmer error)
 */
export const createRandomnessState = (seed: number): RandomnessState => {
	if (!Number.isFinite(seed)) {
		throw new RangeError(`A randomness seed must be a finite number; received ${String(seed)}`);
	}

	return {
		drawCount: 0,
		seed: Math.trunc(seed) >>> 0,
		sessionSeed: Math.trunc(seed) >>> 0,
	};
};

/**
 * Draw one uniform value in `[0, 1)`, returning the advanced state.
 *
 * @param state - The current randomness state
 * @returns The drawn value and the next state
 */
export const drawRandom = (state: RandomnessState): RandomDraw => {
	const advancedSeed = (state.seed + MULBERRY32_INCREMENT) >>> 0;

	let scrambled = advancedSeed;

	scrambled = Math.imul(scrambled ^ (scrambled >>> 15), scrambled | 1);
	scrambled ^= scrambled + Math.imul(scrambled ^ (scrambled >>> 7), scrambled | 61);

	return {
		nextState: {
			drawCount: state.drawCount + 1,
			seed: advancedSeed,
			sessionSeed: state.sessionSeed,
		},
		value: ((scrambled ^ (scrambled >>> 14)) >>> 0) / UINT32_RANGE,
	};
};

/**
 * Draw one uniform integer in `[minInclusive, maxInclusive]`, returning the
 * advanced state.
 *
 * @param state - The current randomness state
 * @param minInclusive - Lower bound (integer)
 * @param maxInclusive - Upper bound (integer, ≥ `minInclusive`)
 * @returns The drawn integer and the next state
 * @throws RangeError if the bounds are not integers or are inverted (programmer error)
 */
export const drawIntegerBetween = (
	state: RandomnessState,
	minInclusive: number,
	maxInclusive: number,
): RandomIntegerDraw => {
	if (!Number.isSafeInteger(minInclusive) || !Number.isSafeInteger(maxInclusive)) {
		throw new RangeError(
			`Integer-draw bounds must be safe integers; received ${String(minInclusive)}..${String(maxInclusive)}`,
		);
	}

	if (minInclusive > maxInclusive) {
		throw new RangeError(`Integer-draw bounds are inverted: ${String(minInclusive)} > ${String(maxInclusive)}`);
	}

	const { nextState, value } = drawRandom(state);
	const spanSize = maxInclusive - minInclusive + 1;

	return {
		nextState,
		value: minInclusive + Math.floor(value * spanSize),
	};
};
